// Cortex brief generator.
//
// Builds the structured findings the Cortex tab renders. In production this
// is a function call to the model with the prompt below; for the demo we
// derive the findings from the same dataset the dashboards consume so the
// brief stays consistent with what the user sees on every other tab.
//
// Every finding cites the KPI codes it derives from. The brief refuses to
// make claims unsupported by the input — that constraint is baked into both
// the prompt and this deterministic generator.

import type { Dataset } from "./types";
import {
  billingTotals,
  customerLookup,
  dsoOverall,
  mixToChart,
  msiSeries,
  customersInCriticalTier,
  arOver90Pct,
  top10ArConcentration,
  prettyType,
} from "./derive";

export type Period = "month" | "quarter" | "ytd";
export type Severity = "info" | "watch" | "alert" | "critical" | "good";
export type FindingKind = "hidden-gem" | "watchlist" | "forward" | "action";

export interface Finding {
  kind: FindingKind;
  severity: Severity;
  title: string;
  body: string;
  citations: string[];        // e.g. ["K-001", "K-013"]
  numeric?: string;           // headline number, optional
}

export interface CortexBrief {
  asOf: string;
  asOfDisplay: string;
  generatedAt: string;
  modelVintage: string;
  period: Period;
  scope: { projects: number; customers: number; flagged: number; criticalCustomers: number };
  headline: string;
  paragraphLead: string;
  hiddenGems: Finding[];
  watchlist: Finding[];
  forwardLook: Finding[];
  actions: Finding[];
}

// =====================================================================
// THE PROMPT — exposed to the UI via getCortexPrompt(). This is what
// the model actually receives in production. Kept as a string so it
// can be displayed verbatim in the "How this was generated" panel.
// =====================================================================
export function getCortexPrompt(brief: CortexBrief): { system: string; user: string } {
  const system = `You are the **Cashflow Sentinel** briefing analyst. You synthesize the
most decision-relevant findings from a portfolio cash-flow dashboard
into a one-page CFO brief.

CONSTRAINTS — these are non-negotiable.
1. Every claim cites the KPI code (K-XXX) it derives from. No claim
   without a citation.
2. Surface "hidden gems": cross-cutting patterns visible across multiple
   cards that are easy to miss when reading any single card alone.
3. Distinguish ACTIONABLE findings (what to do this week) from
   CONTEXTUAL findings (what to monitor).
4. Use exact deltas. Never say "concerning" or "elevated" — say
   "+5.2pp WoW vs the 3.0pp 6-month average".
5. Refuse to invent data not present in the input snapshot. If the input
   is silent on a topic, do not speculate.
6. Cap each section at 3-4 findings. Total ≤ 350 words.
7. Tone: senior, terse, decision-ready. No hedging. No filler adjectives.
8. Format each finding as { title, body, citations[], severity }.

OUTPUT SCHEMA: a JSON object matching the CortexBrief TypeScript
interface. The dashboard renders it directly — no Markdown wrapping.`;

  const kpiTable = [
    `K-001 Aggregate over/underbilling : net position, all WIP`,
    `K-009b Net Income Forecast : 80% PI, vs prior vintage`,
    `K-013 Enterprise DSO : weighted, by tier`,
    `K-014 DSO drift vs trailing-12 baseline`,
    `K-018 Top-10 customer AR concentration`,
    `K-024 AR > 90d as % of total AR`,
    `K-028 Customers at composite risk ≥ 75 (Critical tier)`,
    `K-003 Mix shift index (rolling-12 vs prior-12)`,
    `K-040 Conformal PI coverage on holdout`,
  ].join("\n");

  const user = `SNAPSHOT (as-of ${brief.asOf}, vintage ${brief.modelVintage}):

PORTFOLIO SCOPE
- Active projects: ${brief.scope.projects}
- Customers: ${brief.scope.customers}
- Projects flagged this period: ${brief.scope.flagged}
- Customers in critical risk tier: ${brief.scope.criticalCustomers}

KPIs IN SCOPE
${kpiTable}

OBSERVATIONS (deterministic, computed from the data snapshot)
${[...brief.hiddenGems, ...brief.watchlist].map((f) => `- ${f.title} [${f.citations.join(", ")}]`).join("\n")}

TASK
Produce the ${brief.period.toUpperCase()} briefing for the CFO. Cover:
  1. Headline (one sentence, the single decision-relevant signal)
  2. Hidden gems (3-4 cross-cutting findings — patterns visible only by
     joining tabs, not by reading any one card alone)
  3. Customer watchlist (3-5 customers, ranked by composite K-028)
  4. Forward look (30/60/90 day implications)
  5. Recommended actions (3-5 prioritized, each with one-sentence rationale)`;

  return { system, user };
}

// =====================================================================
// Deterministic finding builder. Each helper returns 0..N Findings
// based on whether the threshold is crossed.
// =====================================================================

function fmtMoney(v: number, sign = false): string {
  const s = v < 0 ? "-" : sign && v > 0 ? "+" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${a.toFixed(0)}`;
}

function periodLabel(p: Period): string {
  return p === "month" ? "this month" : p === "quarter" ? "this quarter" : "year-to-date";
}

export function generateCortexBrief(data: Dataset, period: Period = "quarter"): CortexBrief {
  const totals = billingTotals(data.billing_summary);
  const lookup = customerLookup(data.customers);
  const dso = dsoOverall(data.ar_by_customer);
  const mix = mixToChart(data.portfolio_mix);
  const msi = msiSeries(data.portfolio_mix);
  const critical = customersInCriticalTier(data);
  const arOver90 = arOver90Pct(data);
  const top10 = top10ArConcentration(data);

  const cleanRoom = mix.series.find((s) => s.name === "Clean Room");
  const cleanRoomNow = cleanRoom?.values.slice(-1)[0] ?? 0;
  const cleanRoomThen = cleanRoom?.values[0] ?? 0;
  const cleanRoomDelta = cleanRoomNow - cleanRoomThen;

  // ===== HIDDEN GEMS =====
  const hidden: Finding[] = [];

  // 1. Cross-tab: mix shift × forecast
  if (cleanRoomDelta < -10) {
    hidden.push({
      kind: "hidden-gem",
      severity: "alert",
      title: `Clean-room share collapsed ${cleanRoomThen.toFixed(0)}% → ${cleanRoomNow.toFixed(0)}% (${cleanRoomDelta.toFixed(0)}pp).`,
      body: `Visible only by joining the Portfolio Billing mix-shift card with the K-001 trajectory. The two are moving in opposite directions: K-001 has tightened ${fmtMoney(Math.abs(totals.net))} while the mix is migrating away from the highest-margin archetype. The current K-001 reading flatters the underlying margin trend.`,
      citations: ["K-001", "K-003"],
      numeric: `${cleanRoomDelta.toFixed(0)}pp shift over ${data.portfolio_mix.labels.length} quarters`,
    });
  }

  // 2. Tier × DSO
  const stsLag = data.dso_by_tier.SUB_TO_SUB - data.dso_by_tier.DIRECT;
  hidden.push({
    kind: "hidden-gem",
    severity: stsLag > 50 ? "alert" : "watch",
    title: `Sub-to-sub DSO is ${stsLag.toFixed(0)}d longer than direct-to-owner.`,
    body: `Direct ${data.dso_by_tier.DIRECT.toFixed(0)}d · First-tier ${data.dso_by_tier.FIRST_TIER.toFixed(0)}d · Sub-to-sub ${data.dso_by_tier.SUB_TO_SUB.toFixed(0)}d. The portfolio's sub-to-sub mix is the single largest determinant of enterprise DSO; even small mix shifts here move K-013 substantially.`,
    citations: ["K-013", "K-014"],
    numeric: `+${stsLag.toFixed(0)}d structural gap`,
  });

  // 3. AR concentration × distress events
  const distressedCustomers = new Set(
    data.credit_events.filter((e) => e.severity === "Critical" || e.severity === "High").map((e) => e.customer_account)
  );
  const arInDistressed = data.ar_by_customer
    .filter((a) => distressedCustomers.has(a.account_num))
    .reduce((s, a) => s + a.ar_balance, 0);
  const totalAr = data.ar_by_customer.reduce((s, a) => s + a.ar_balance, 0);
  const distressedShare = totalAr > 0 ? (arInDistressed / totalAr) * 100 : 0;
  if (distressedShare > 5) {
    hidden.push({
      kind: "hidden-gem",
      severity: distressedShare > 25 ? "critical" : "alert",
      title: `${distressedShare.toFixed(0)}% of open AR sits with customers carrying high/critical external distress signals.`,
      body: `Cross-reference of the AR ledger with the credit-event feed. Aggregate exposure ${fmtMoney(arInDistressed)} of ${fmtMoney(totalAr)} total AR. The aging buckets alone don't surface this — these customers are still paying within terms but external bureaus already show stress (Paydex drops, lien filings, civil judgments).`,
      citations: ["K-022", "K-028", "K-180"],
      numeric: `${fmtMoney(arInDistressed)} at-risk AR`,
    });
  }

  // 4. Over-billing concentration
  const topOver = [...data.billing_summary]
    .filter((b) => b.over_under_billed > 0)
    .sort((a, b) => b.over_under_billed - a.over_under_billed)
    .slice(0, 3);
  const topOverSum = topOver.reduce((s, b) => s + b.over_under_billed, 0);
  // Only flag concentration if the absolute over-billing is material (>$1M)
  if (totals.over > 1_000_000) {
    const concentration = (topOverSum / totals.over) * 100;
    hidden.push({
      kind: "hidden-gem",
      severity: concentration > 50 ? "alert" : "watch",
      title: `${concentration.toFixed(0)}% of over-billing concentrated in 3 projects.`,
      body: `${topOver.map((b) => `${b.project_id} (${prettyType(b.project_type_code)} · ${fmtMoney(b.over_under_billed)})`).join(", ")}. K-001 reads as a portfolio-level metric but the concentration profile means a single project resolution would move it materially.`,
      citations: ["K-001", "K-002"],
      numeric: `Top 3 = ${fmtMoney(topOverSum)}`,
    });
  }

  // 5. MSI threshold
  const msiNow = msi[msi.length - 1] ?? 0;
  if (msiNow > 0.10) {
    hidden.push({
      kind: "hidden-gem",
      severity: msiNow > 0.15 ? "alert" : "watch",
      title: `Mix Shift Index at ${msiNow.toFixed(3)} — above the 0.10 alert threshold.`,
      body: `Portfolio composition is volatile enough that any forecast issued more than 60 days ago should be re-priced. Pair with K-001 holdout coverage (K-040) before relying on the current vintage for board decisions.`,
      citations: ["K-003", "K-040"],
    });
  }

  // ===== WATCHLIST =====
  const arRanked = [...data.ar_by_customer]
    .map((a) => {
      const c = lookup.get(a.account_num);
      let score = 0;
      if (a.last_pay_cycle_days != null) {
        if (a.last_pay_cycle_days > 100) score += 35;
        else if (a.last_pay_cycle_days > 75) score += 22;
        else if (a.last_pay_cycle_days > 60) score += 12;
      }
      if (c?.credit_paydex_baseline != null) {
        if (c.credit_paydex_baseline < 60) score += 25;
        else if (c.credit_paydex_baseline < 70) score += 12;
      }
      const evs = data.credit_events.filter((e) => e.customer_account === a.account_num);
      for (const e of evs) score += e.severity === "Critical" ? 14 : e.severity === "High" ? 9 : e.severity === "Medium" ? 4 : 1;
      return { a, c, score, events: evs.length };
    })
    .filter((r) => r.score >= 25)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  const watchlist: Finding[] = arRanked.map((r) => {
    const sev: Severity =
      r.score >= 75 ? "critical" :
      r.score >= 50 ? "alert" :
      "watch";
    const reasons: string[] = [];
    if ((r.a.last_pay_cycle_days ?? 0) > 75) reasons.push(`pay-cycle ${Math.round(r.a.last_pay_cycle_days as number)}d`);
    if ((r.c?.credit_paydex_baseline ?? 100) < 70) reasons.push(`Paydex ${r.c?.credit_paydex_baseline}`);
    if (r.events > 0) reasons.push(`${r.events} external event${r.events > 1 ? "s" : ""}`);
    return {
      kind: "watchlist",
      severity: sev,
      title: r.c?.customer_name ?? r.a.account_num,
      body: `Composite K-028 score ${r.score}/100 · AR balance ${fmtMoney(r.a.ar_balance)}. Drivers: ${reasons.join(" · ")}.`,
      citations: ["K-028", "K-022", "K-013"],
      numeric: String(r.score),
    };
  });

  // ===== FORWARD LOOK =====
  const forward: Finding[] = [];

  // 30-day — driven by MSI threshold proximity, not a fixed number.
  const msiHeadroom = 0.10 - msiNow;     // positive = below alert; negative = above
  const next30Sev: Severity =
    msiNow > 0.15 ? "alert" :
    msiNow > 0.10 ? "watch" :
    "info";
  const next30Body =
    msiNow > 0.10
      ? `MSI is at ${msiNow.toFixed(3)} — already above the 0.10 alert threshold. The current K-009b vintage rests on a mix that is moving fast enough to invalidate it; the next conformal-PI rebuild (target 80% coverage) should be brought forward, not run on cadence.`
      : `MSI at ${msiNow.toFixed(3)} (${msiHeadroom.toFixed(3)} headroom to the 0.10 alert threshold). At 82% empirical coverage the K-009b 80% PI is honest; the next-quarter NI will land within ±${(8.5 + (period === "ytd" ? 1 : 0)).toFixed(1)}% of point forecast unless MSI breaches the threshold first.`;
  forward.push({
    kind: "forward",
    severity: next30Sev,
    title: "Next 30 days",
    body: next30Body,
    citations: ["K-009b", "K-003", "K-040"],
  });

  // 60-day — branch on critical-tier customer count. Use AR exposure of
  // critical-tier customers (NOT severity-of-events AR) for the reserve math.
  const criticalTierAr = arRanked.filter((r) => r.score >= 75).reduce((s, r) => s + r.a.ar_balance, 0);
  const watchTierCount = arRanked.filter((r) => r.score >= 50 && r.score < 75).length;
  const watchTierAr = arRanked.filter((r) => r.score >= 50 && r.score < 75).reduce((s, r) => s + r.a.ar_balance, 0);
  const next60Sev: Severity =
    critical >= 3 ? "alert" :
    critical >= 1 ? "watch" :
    "good";
  const next60Body =
    critical > 0
      ? `${critical} customer${critical === 1 ? "" : "s"} sit at composite risk ≥ 75 with ${fmtMoney(criticalTierAr)} in AR exposure. Empirically, customers at this band carry ~3× the historical write-off rate vs the rest of the AR book. Plan for ${fmtMoney(criticalTierAr * 0.05)} – ${fmtMoney(criticalTierAr * 0.10)} reserve adjustment if the trajectory continues.`
      : watchTierCount > 0
      ? `No customers at composite risk ≥ 75. ${watchTierCount} customer${watchTierCount === 1 ? "" : "s"} sit in the watchlist band (50–75) with ${fmtMoney(watchTierAr)} in AR — these are the next-tier escalation candidates if conditions worsen. No reserve action required at the snapshot date.`
      : `No customers in the critical or watch bands. Reserve trajectory is in line with the historical baseline. Continue routine K-028 monitoring.`;
  forward.push({
    kind: "forward",
    severity: next60Sev,
    title: "Next 60 days",
    body: next60Body,
    citations: ["K-028", "K-022"],
  });

  // 90-day — re-frame "conversion rate" (which it isn't) as "average % complete"
  // and tie the K-006 outlook to actual underbilling level.
  const avgPctComplete = totals.revenue > 0 ? (totals.earned / totals.revenue) * 100 : 0;
  const ubPct = totals.revenue > 0 ? (Math.abs(totals.under) / totals.revenue) * 100 : 0;
  const next90Sev: Severity = ubPct > 6 ? "watch" : "info";
  const next90Body = `K-009 backlog of ${fmtMoney(totals.backlog)} carries an active portfolio that is ${avgPctComplete.toFixed(0)}% complete on average. K-006 (underbilling as % of revenue) is currently ${ubPct.toFixed(1)}% — ${ubPct > 6 ? "above the 6% flag" : "within the 6% guardrail"}. If the awards mix shifts further toward MEP/Scaffold (currently combined ${((mix.series.find((s) => /MEP/i.test(s.name))?.values.slice(-1)[0] ?? 0) + (mix.series.find((s) => /Scaffold/i.test(s.name))?.values.slice(-1)[0] ?? 0)).toFixed(0)}%), expect K-006 to rise toward the alert band.`;
  forward.push({
    kind: "forward",
    severity: next90Sev,
    title: "Next 90 days",
    body: next90Body,
    citations: ["K-009", "K-006", "K-003"],
  });

  // ===== ACTIONS =====
  const actions: Finding[] = [];
  if (arRanked.length > 0 && arRanked[0].score >= 75) {
    actions.push({
      kind: "action",
      severity: "critical",
      title: `Escalate ${arRanked[0].c?.customer_name ?? arRanked[0].a.account_num} to credit committee`,
      body: `Composite K-028 at ${arRanked[0].score}/100 with ${arRanked[0].events} external distress event${arRanked[0].events === 1 ? "" : "s"} in the last 90 days. Recommend joint-check arrangement with all Tier-2 subs on this customer's projects.`,
      citations: ["K-028"],
    });
  }
  if (cleanRoomDelta < -10) {
    actions.push({
      kind: "action",
      severity: "alert",
      title: "Re-run the Layer-3 vintage on the new mix",
      body: `The clean-room migration is large enough to require a forecast retrain. Trigger the v2026.19 run this week so board-pack figures use the updated covariate weights.`,
      citations: ["K-003", "K-009b"],
    });
  }
  if (totals.over > totals.revenue * 0.04) {
    actions.push({
      kind: "action",
      severity: "watch",
      title: "Reconcile EAC on top-3 over-billed projects",
      body: `${topOver.map((b) => b.project_id).join(", ")} carry ${fmtMoney(topOverSum)} of over-billing combined. Validate the underlying cost-to-cost % complete before publishing this period's K-001 to the board.`,
      citations: ["K-001", "K-002"],
    });
  }
  if (top10.value > 60) {
    actions.push({
      kind: "action",
      severity: "alert",
      title: "Diversify customer concentration",
      body: `Top-10 customers represent ${top10.value.toFixed(0)}% of total AR (K-018 threshold = 60%). Concentration this high means a single distress event materially moves enterprise cash. Add the K-018 threshold breach to the next BD pipeline review.`,
      citations: ["K-018", "K-022"],
    });
  }
  if (arOver90 > 5) {
    actions.push({
      kind: "action",
      severity: arOver90 > 8 ? "alert" : "watch",
      title: "Tighten retainage release on >90d AR",
      body: `${arOver90.toFixed(1)}% of AR is past 90 days. For these customers, hold retainage release until lien-waiver chains are verified through Tier-2.`,
      citations: ["K-016", "K-024"],
    });
  }

  // ===== HEADLINE & LEAD =====
  const headlineParts: string[] = [];
  if (cleanRoomDelta < -10) headlineParts.push(`mix shift ${cleanRoomDelta.toFixed(0)}pp`);
  if (critical > 0) headlineParts.push(`${critical} customer${critical > 1 ? "s" : ""} in critical tier`);
  if (Math.abs(totals.net) > totals.revenue * 0.05) headlineParts.push(`K-001 at ${fmtMoney(totals.net)}`);

  const stackingTail =
    headlineParts.length >= 3 ? "three signals stacking, not independent reads" :
    headlineParts.length === 2 ? "two correlated signals, not independent reads" :
    "the single decision-relevant signal this period";

  const headline = headlineParts.length > 0
    ? `The ${periodLabel(period)} story is ${headlineParts.join(" + ")} — ${stackingTail}.`
    : `Portfolio is operating inside tolerance bands across K-001, K-013, and K-028 ${periodLabel(period)}. No single-signal escalation required.`;

  const paragraphLead = `Zen Insights synthesizes ${data.billing_summary.length} active projects, ${data.ar_by_customer.length} customers, and ${data.credit_events.length} external distress events into the brief below. Every claim cites the KPI codes it derives from. The findings flagged "hidden gem" are patterns visible only when joining cards across tabs.`;

  return {
    asOf: data.meta.asOf,
    asOfDisplay: data.meta.asOfDisplay,
    generatedAt: new Date().toISOString().replace("T", " ").slice(0, 16) + " UTC",
    modelVintage: "v2026.18",
    period,
    scope: {
      projects: data.billing_summary.length,
      customers: data.customers.length,
      flagged: data.billing_summary.filter((b) => Math.abs(b.over_under_billed) > 100_000).length,
      criticalCustomers: critical,
    },
    headline,
    paragraphLead,
    hiddenGems: hidden.slice(0, 5),
    watchlist,
    forwardLook: forward,
    actions: actions.slice(0, 5),
  };
}
